import { logger } from "@/lib/logger";
import AppRoutes from "@/constants/routes/AppRoutes";
import useUsers from "@/hooks/useUsers";
import useWorkspace from "@/hooks/useWorkspace";
import { Permission } from "@/lib/permissions/Permission.enum";
import { useHasPermission } from "@/lib/permissions/permissionFunctions";
import Workspace, { WorkspaceRole } from "@/models/workspace/Workspace";
import { Item } from "@/pages/workspace/utils/workspaceItemUtils";
import {
  canDeleteItem,
  canEditMetadata,
  canMoveItems,
  canReindexItem,
  canUploadFiles,
  getDisplayWorkspaceRole,
  isWorkspaceAdmin,
  isOrphanedWorkspace,
  isWorkspaceOwner,
} from "@/pages/workspace/utils/workspaceAccess";
import { User } from "@/models/users/User";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  WorkspaceContext,
  type UpdateUploadedFileResult,
} from "./WorkspaceContext";
import { extractWorkspaceFiltersWithCategories } from "@/pages/workspace/utils/workspaceUtils";
import {
  filterItemsForSearch,
  getAllWorkspaceItems,
  isItemIndexable,
  pickTextOnlyMatches,
} from "@/pages/workspace/utils/workspaceItemUtils";
import { useWorkspaceTextMatches } from "@/pages/workspace/hooks/useWorkspaceTextMatches";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import useAzureStorage from "../hooks/useAzureStorage";
import { useMicrosoftDocxPdfStaging } from "../hooks/useMicrosoftDocxPdfStaging";
import { UploadSource } from "@/components/upload/types/upload-item";

export interface WorkspaceSearch {
  searchString: string;
  folders: { id: string; name: string }[];
  searchOnMetadata: boolean;
}
interface WorkspaceProviderProps {
  workspaceId: string;
  children: React.ReactNode;
}

/**
 * WorkspaceProvider handles fetching the workspace and knowing the user's permissions.
 */
export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({
  workspaceId,
  children,
}) => {
  const navigate = useNavigate();
  const {
    useQuerySingle,
    removeDocuments,
    updateDocumentMetadata,
    syncDocument,
  } = useWorkspace();
  const { useQueryAllTenantUsers, useQueryMe } = useUsers();
  const { createNewVersionOfFileInAzure } = useAzureStorage();
  const { stageMicrosoftPdfIfNeeded } = useMicrosoftDocxPdfStaging();
  const hasPermissions = useHasPermission();
  const canManageOrphanedWorkspaces = hasPermissions({
    allOf: [Permission.ManageOrphanedWorkspaces],
  });

  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [tenantUsers, setTenantUsers] = useState<User[]>([]);
  const [workspaceOwner, setWorkspaceOwner] = useState<User | undefined>();
  const [myUser, setMyUser] = useState<User | undefined>();
  const [isUserOwner, setIsUserOwner] = useState(false);
  const [isParentWorkspace, setIsParentWorkspace] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const fetchedWorkspace = useQuerySingle(workspaceId);
  const fetchedUsers = useQueryAllTenantUsers();
  const fetchedMe = useQueryMe();

  // Search states
  const [search, setSearch] = useState<WorkspaceSearch>({
    searchString: "",
    folders: [],
    searchOnMetadata: false,
  });
  const [filterOptions, setFilterOptions] = useState<{
    metadataNames: string[];
    folders: { id: string; name: string }[];
  }>({ metadataNames: [], folders: [] });
  const [selectedMetadataFilters, setSelectedMetadataFilters] = useState<{
    metadataNames: string[];
  }>({ metadataNames: [] });

  // Memoize the search filters to prevent unnecessary re-renders
  const memoizedFilterOptions = useMemo(() => {
    if (fetchedWorkspace.isFetched && fetchedWorkspace.data) {
      return extractWorkspaceFiltersWithCategories(fetchedWorkspace.data);
    }
    return { metadataNames: [], folders: [] };
  }, [fetchedWorkspace.isFetched, fetchedWorkspace.data]);

  useEffect(() => {
    if (
      fetchedWorkspace.isFetched &&
      fetchedWorkspace.data &&
      fetchedUsers.isFetched &&
      fetchedUsers.data &&
      fetchedMe.isFetched &&
      fetchedMe.data
    ) {
      setWorkspace(fetchedWorkspace.data);
      setTenantUsers(fetchedUsers.data);
      setMyUser(fetchedMe.data);

      setIsUserOwner(fetchedWorkspace.data.userId === fetchedMe.data.id);
      setIsParentWorkspace(fetchedWorkspace.data.isParentWorkspace || false);

      const owner = fetchedUsers.data.find(
        (u) => u.id === fetchedWorkspace.data?.userId
      );
      setWorkspaceOwner(owner);
    } else if (fetchedWorkspace.error || fetchedUsers.error) {
      navigate(AppRoutes.workspaceNotFound, { replace: true });
    }
  }, [fetchedWorkspace, fetchedUsers, fetchedMe, navigate]);

  // Separate useEffect for search filters to avoid dependency issues
  useEffect(() => {
    setFilterOptions(memoizedFilterOptions);
  }, [memoizedFilterOptions]);

  // Workspace-wide search and filtering logic. Names match as typed; documents whose
  // text matches join once the search index answers (LAR-235).
  const { textMatches } = useWorkspaceTextMatches(
    workspace?.id,
    search.searchString
  );
  const textMatchIds = useMemo(
    () => new Set(textMatches.keys()),
    [textMatches]
  );

  const filteredWorkspaceItems = useMemo(() => {
    if (!workspace) return [];

    const allItems = getAllWorkspaceItems(workspace);
    if (!search.searchString.trim()) return allItems;

    return filterItemsForSearch(allItems, search, textMatchIds);
  }, [workspace, search, textMatchIds]);

  const textOnlyMatches = useMemo(
    () =>
      workspace
        ? pickTextOnlyMatches(
            getAllWorkspaceItems(workspace),
            search,
            textMatches
          )
        : new Map(),
    [workspace, search, textMatches]
  );

  const isOrphanedInspection = useMemo(
    () =>
      Boolean(
        workspace &&
        isOrphanedWorkspace(workspace) &&
        canManageOrphanedWorkspaces
      ),
    [workspace, canManageOrphanedWorkspaces]
  );

  const effectiveRole = useMemo((): WorkspaceRole | null => {
    if (!workspace || !myUser) return null;
    if (isOrphanedInspection) return WorkspaceRole.READ;
    if (isParentWorkspace) return WorkspaceRole.READ;
    return getDisplayWorkspaceRole(workspace, myUser.id);
  }, [workspace, myUser, isParentWorkspace, isOrphanedInspection]);

  const canUserUploadFiles = useCallback(() => {
    if (!workspace || !myUser || isOrphanedInspection) return false;
    return canUploadFiles(workspace, myUser.id, isParentWorkspace);
  }, [workspace, myUser, isParentWorkspace, isOrphanedInspection]);

  const canUserMoveItems = useCallback(() => {
    if (!workspace || !myUser || isOrphanedInspection) return false;
    return canMoveItems(workspace, myUser.id, isParentWorkspace);
  }, [workspace, myUser, isParentWorkspace, isOrphanedInspection]);

  const canUserEditMetadata = useCallback(
    (_item: Item) => {
      if (!workspace || !myUser || isOrphanedInspection) return false;
      return canEditMetadata(workspace, myUser.id, isParentWorkspace);
    },
    [workspace, myUser, isParentWorkspace, isOrphanedInspection]
  );

  const canUserDeleteFile = useCallback(
    (item: Item) => {
      if (!workspace || !myUser || isOrphanedInspection) return false;
      return canDeleteItem(workspace, myUser.id, item, isParentWorkspace);
    },
    [workspace, myUser, isParentWorkspace, isOrphanedInspection]
  );

  const isUserAdmin = useCallback(() => {
    if (!workspace || !myUser || isOrphanedInspection) return false;
    return isWorkspaceAdmin(workspace, myUser.id, isParentWorkspace);
  }, [workspace, myUser, isParentWorkspace, isOrphanedInspection]);

  const canChangeOrgSharing = useCallback(() => {
    if (!workspace || !myUser || isOrphanedInspection) return false;
    return (
      isWorkspaceAdmin(workspace, myUser.id, isParentWorkspace) &&
      hasPermissions({ allOf: [Permission.UpdateWorkspaceSharingOrgShared] })
    );
  }, [
    workspace,
    myUser,
    isParentWorkspace,
    hasPermissions,
    isOrphanedInspection,
  ]);

  const canDeleteWorkspace = useCallback(() => {
    if (!workspace || !myUser || isOrphanedInspection) return false;
    return isWorkspaceOwner(workspace, myUser.id);
  }, [workspace, myUser, isOrphanedInspection]);

  const handleDeleteDocuments = useCallback(
    async (
      workspaceId: string,
      documentIds: string[],
      options?: {
        successToastMessage?: string | null;
        errorToastMessage?: string;
      }
    ) => {
      try {
        await removeDocuments.mutateAsync({
          workspaceId,
          requestBody: {
            documentIds,
          },
        });
        // Show success toast with custom message or default
        // Pass null to suppress toast entirely
        if (options?.successToastMessage !== null) {
          const message =
            options?.successToastMessage || "Filen er blevet slettet";
          showSuccessToast(message);
        }
      } catch {
        const message =
          options?.errorToastMessage || "Fejl ved sletning af fil";
        showErrorToast(message);
      }
    },
    [removeDocuments]
  );

  const handleUpdateDocumentMetadata = useCallback(
    async (
      documentId: string,
      metadata: {
        documentSource: string;
        driveId?: string;
        siteId?: string;
        sharepointFileId?: string;
      }
    ) => {
      try {
        await updateDocumentMetadata.mutateAsync({
          workspaceId,
          documentId,
          requestBody: metadata,
        });
      } catch {
        showErrorToast("Fejl ved opdatere filens metadata");
      }
    },
    [updateDocumentMetadata, workspaceId]
  );

  const handleUpdateUploadedFile = async (
    file: File,
    documentId: string
  ): Promise<UpdateUploadedFileResult> => {
    try {
      const newBlobName = await createNewVersionOfFileInAzure(
        file,
        workspaceId
      );

      const document = workspace
        ? getAllWorkspaceItems(workspace).find(
            (item) => "blobName" in item && item.id === documentId
          )
        : undefined;

      if (
        document &&
        "documentSource" in document &&
        document.documentSource !== UploadSource.LOCAL
      ) {
        await stageMicrosoftPdfIfNeeded(file.name, newBlobName, workspaceId, {
          driveId: document.driveId,
          siteId: document.siteId,
          sharepointFileId: document.sharepointFileId,
          source: document.documentSource,
        });
      }

      await syncDocument.mutateAsync({
        workspaceId,
        documentId,
        requestBody: {
          newBlobName,
          newDocumentName: file.name,
        },
      });
      return { ok: true };
    } catch (error) {
      logger.error("Failed to update workspace document from its cloud copy", {
        operation: "updateUploadedFile",
        error,
        workspaceId,
        documentId,
      });
      // The mutation rethrows the API's message, which is its own Danish wording when
      // it refused the update (a remote name a sibling already has); the callers show it.
      return {
        ok: false,
        message:
          error instanceof Error && error.message.trim().length > 0
            ? error.message
            : "Kunne ikke opdatere filen",
      };
    }
  };

  const canItemBeReIndexed = useCallback(
    (item: Item) => {
      if (!workspace || !myUser || isOrphanedInspection) return false;
      if (!canReindexItem(workspace, myUser.id, isParentWorkspace))
        return false;
      if (!item.revisions || !item.activeRevision) return false;
      const activeRevision = item.revisions.find(
        (r) => r.revisionVersion === item.activeRevision
      );
      return (
        (activeRevision?.isIndexed === false ||
          activeRevision?.isIndexed === undefined ||
          activeRevision?.searchIndexStale === true) &&
        isItemIndexable(item, undefined)
      );
    },
    [workspace, myUser, isParentWorkspace, isOrphanedInspection]
  );

  return (
    <WorkspaceContext.Provider
      value={{
        workspace,
        tenantUsers,
        myUser,
        workspaceOwner,
        isUserOwner,
        isParentWorkspace,
        isOrphanedInspection,
        effectiveRole,
        canUserUploadFiles,
        canUserMoveItems,
        canUserEditMetadata,
        canUserDeleteFile,
        isUserAdmin,
        canChangeOrgSharing,
        canDeleteWorkspace,
        setLoading,
        isLoading:
          fetchedWorkspace.isPending || fetchedUsers.isPending || isLoading,
        search,
        setSearch,
        metadataOptions: filterOptions.metadataNames,
        folderOptions: filterOptions.folders,
        selectedMetadataFilters,
        setSelectedMetadataFilters,
        filteredWorkspaceItems,
        textMatchIds,
        textOnlyMatches,
        handleDeleteDocuments,
        handleUpdateDocumentMetadata,
        handleUpdateUploadedFile,
        canItemBeReIndexed,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};
